import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CurrencyDollarIcon,
  FunnelIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChartBarIcon,
  CheckIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';

import MainLayout from '../components/Layout/MainLayout';
import { useAuth } from '../contexts/AuthContext';
import useDebounce from '../hooks/useDebounce';
import useSubmissionProtection from '../hooks/useSubmissionProtection';
import {
  Input,
  Select,
  Button,
  Modal,
  showToast,
  ConfirmDialog
} from '../components/UI';
import Badge from '../components/UI/Badge';
import priceSlabService from '../services/priceSlabService';
import { boreSizeService } from '../services/boreSizeService';
import { PriceSlab, PriceSlabFormData } from '../types/priceSlab';
import { BoreSize } from '../types/boreSize';

interface PriceSlabFormDataWithEdit extends PriceSlabFormData {
  isEdit?: boolean;
}

interface TableParams {
  page: number;
  limit: number;
  search: string;
  boreSizeId?: number;
  status: 'all' | 'active' | 'cancelled';
}

interface GroupedPriceSlabs {
  boreSize: BoreSize;
  slabs: PriceSlab[];
  totalSlabs: number;
  minRate: number;
  maxRate: number;
  maxDepth: number;
}

interface BulkEditSlab {
  id?: number;
  from_slab: number;
  to_slab: number;
  rate: number;
  is_cancelled: 'Y' | 'N';
  _isNew?: boolean;
}

interface BulkEditForm {
  bore_size_id: number;
  slabs: BulkEditSlab[];
}

// Validation schema for single slab
const priceSlabSchema: yup.ObjectSchema<PriceSlabFormDataWithEdit> = yup.object().shape({
  bore_size_id: yup
    .number()
    .required('Bore size is required')
    .positive('Please select a bore size'),
  from_slab: yup
    .number()
    .min(0, 'From slab must be 0 or greater')
    .required('From slab is required'),
  to_slab: yup
    .number()
    .min(0, 'To slab must be 0 or greater')
    .required('To slab is required')
    .test('greater-than-from', 'To slab must be greater than from slab', function(value) {
      return value! > this.parent.from_slab;
    }),
  rate: yup
    .number()
    .min(0, 'Rate must be 0 or greater')
    .required('Rate is required'),
  is_cancelled: yup.string().oneOf(['Y', 'N']).optional(),
  isEdit: yup.boolean().optional()
});

// Validation schema for bulk edit
const bulkEditSchema = yup.object().shape({
  bore_size_id: yup.number().required(),
  slabs: yup.array().of(
    yup.object().shape({
      id: yup.number().optional(),
      from_slab: yup.number().min(0, 'From slab must be 0 or greater').required('From slab is required'),
      to_slab: yup.number().min(0, 'To slab must be 0 or greater').required('To slab is required')
        .test('greater-than-from', 'To slab must be greater than from slab', function(value) {
          return value! > this.parent.from_slab;
        }),
      rate: yup.number().min(0, 'Rate must be 0 or greater').required('Rate is required'),
      is_cancelled: yup.string().oneOf(['Y', 'N']).required(),
      _isNew: yup.boolean().optional()
    })
  )
});

const PriceSlabManagement: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [priceSlabs, setPriceSlabs] = useState<PriceSlab[]>([]);
  const [boreSizes, setBoreSizes] = useState<BoreSize[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPriceSlab, setEditingPriceSlab] = useState<PriceSlab | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const [selectedBoreSizeForAdd, setSelectedBoreSizeForAdd] = useState<number | null>(null);
  const [bulkEditMode, setBulkEditMode] = useState<number | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; priceSlab: PriceSlab | null }>({
    show: false,
    priceSlab: null
  });
  const [deleting, setDeleting] = useState(false);

  // Pagination and filter states
  const [tableParams, setTableParams] = useState<TableParams>({
    page: 1,
    limit: 1000, // Load all for grouping
    search: '',
    status: 'all'
  });

  const [searchInput, setSearchInput] = useState('');

  const loadingRef = useRef(false);

  // Debounce search
  const debouncedSearch = useDebounce(searchInput, 500);

  // Form handling for single slab
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    control,
    watch
  } = useForm<PriceSlabFormDataWithEdit>({
    resolver: yupResolver(priceSlabSchema),
    defaultValues: {
      bore_size_id: 0,
      from_slab: 0,
      to_slab: 0,
      rate: 0,
      is_cancelled: 'N',
      isEdit: false
    }
  });

  const selectedBoreSizeId = watch('bore_size_id');

  // Form handling for bulk edit
  const bulkEditForm = useForm<BulkEditForm>({
    resolver: yupResolver(bulkEditSchema) as any,
    defaultValues: {
      bore_size_id: 0,
      slabs: []
    }
  });

  const { fields, append, remove } = useFieldArray({
    control: bulkEditForm.control,
    name: 'slabs'
  });

  // Submission protection
  const { protectedSubmit } = useSubmissionProtection();

  // Load bore sizes
  const loadBoreSizes = useCallback(async () => {
    try {
      const response = await boreSizeService.getBoreSizes({ limit: 1000 });
      const allBoreSizes = response.data || [];
      setBoreSizes(allBoreSizes);
      // Collapse all groups by default
      setExpandedGroups(new Set());
    } catch (error: any) {
      console.error('Error loading bore sizes:', error);
      showToast.error('Failed to load bore sizes');
    }
  }, []);

  // Load price slabs with pagination and filters
  const loadPriceSlabs = useCallback(async (params: TableParams) => {
    if (loadingRef.current) return;
    loadingRef.current = true;

    try {
      setLoading(true);

      const response = await priceSlabService.getPriceSlabs({
        page: params.page,
        limit: params.limit,
        search: params.search,
        boreSizeId: params.boreSizeId,
        status: params.status
      });

      setPriceSlabs(response.priceSlabs || []);
    } catch (error: any) {
      console.error('Error loading price slabs:', error);
      showToast.error(error.response?.data?.message || 'Failed to load price slabs');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, []);

  // Group price slabs by bore size
  const groupedPriceSlabs = useMemo<GroupedPriceSlabs[]>(() => {
    const groups: { [key: number]: GroupedPriceSlabs } = {};

    // Initialize groups for all bore sizes
    boreSizes.forEach(boreSize => {
      groups[boreSize.id] = {
        boreSize,
        slabs: [],
        totalSlabs: 0,
        minRate: 0,
        maxRate: 0,
        maxDepth: 0
      };
    });

    // Populate groups with price slabs
    priceSlabs.forEach(slab => {
      if (slab.bore_size_id && groups[slab.bore_size_id]) {
        groups[slab.bore_size_id].slabs.push(slab);
      }
    });

    // Calculate stats for each group
    Object.values(groups).forEach(group => {
      if (group.slabs.length > 0) {
        group.totalSlabs = group.slabs.length;
        const rates = group.slabs.map(s => s.rate || 0);
        group.minRate = Math.min(...rates);
        group.maxRate = Math.max(...rates);
        const maxToSlab = Math.max(...group.slabs.map(s => s.to_slab || 0));
        group.maxDepth = maxToSlab;

        // Sort slabs by from_slab
        group.slabs.sort((a, b) => (a.from_slab || 0) - (b.from_slab || 0));
      }
    });

    return Object.values(groups).filter(g => {
      const matchesSearch = !tableParams.search ||
        g.boreSize.size.toLowerCase().includes(tableParams.search.toLowerCase());
      return matchesSearch && (g.slabs.length > 0 || tableParams.status === 'all');
    });
  }, [priceSlabs, boreSizes, tableParams.status, tableParams.search]);

  // Get existing slabs for selected bore size in modal
  const existingSlabsForSelectedBoreSize = useMemo(() => {
    if (!selectedBoreSizeId) return [];
    return priceSlabs
      .filter(slab => slab.bore_size_id === selectedBoreSizeId)
      .sort((a, b) => (a.from_slab || 0) - (b.from_slab || 0));
  }, [selectedBoreSizeId, priceSlabs]);

  // Effect for role-based access control - Admin only
  useEffect(() => {
    if (user && user.role !== 'ADMIN') {
      showToast.error('Only administrators can access Price Slab management');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Load initial data
  useEffect(() => {
    if (user && user.role === 'ADMIN') {
      loadBoreSizes();
    }
  }, [user, loadBoreSizes]);

  // Effect for debounced search
  useEffect(() => {
    setTableParams(prev => ({
      ...prev,
      search: debouncedSearch,
      page: 1
    }));
  }, [debouncedSearch]);

  // Effect for loading data when params change
  useEffect(() => {
    if (user && user.role === 'ADMIN') {
      loadPriceSlabs(tableParams);
    }
  }, [tableParams, loadPriceSlabs, user]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Toggle group expansion
  const toggleGroup = (boreSizeId: number) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(boreSizeId)) {
        newSet.delete(boreSizeId);
      } else {
        newSet.add(boreSizeId);
      }
      return newSet;
    });
  };

  // Filter handlers
  const handleBoreSizeFilter = (boreSizeId?: number) => {
    setTableParams(prev => ({ ...prev, boreSizeId, page: 1 }));
  };

  const handleStatusFilter = (status: 'all' | 'active' | 'cancelled') => {
    setTableParams(prev => ({ ...prev, status, page: 1 }));
  };

  // CRUD handlers
  const handleCreate = () => {
    // Initialize bulk create form with one slab
    bulkEditForm.reset({
      bore_size_id: 0,
      slabs: [{
        from_slab: 0,
        to_slab: 50,
        rate: 0,
        is_cancelled: 'N',
        _isNew: true
      }]
    });
    setEditingPriceSlab(null);
    setSelectedBoreSizeForAdd(null);
    setShowModal(true);
  };

  const handleEdit = (priceSlab: PriceSlab) => {
    reset({
      bore_size_id: priceSlab.bore_size_id || 0,
      from_slab: priceSlab.from_slab || 0,
      to_slab: priceSlab.to_slab || 0,
      rate: priceSlab.rate || 0,
      is_cancelled: priceSlab.is_cancelled || 'N',
      isEdit: true
    });
    setEditingPriceSlab(priceSlab);
    setSelectedBoreSizeForAdd(priceSlab.bore_size_id || null);
    setShowModal(true);
  };

  const handleBulkEdit = (group: GroupedPriceSlabs) => {
    const slabs: BulkEditSlab[] = group.slabs.map(slab => ({
      id: slab.id,
      from_slab: slab.from_slab || 0,
      to_slab: slab.to_slab || 0,
      rate: slab.rate || 0,
      is_cancelled: (slab.is_cancelled || 'N') as 'Y' | 'N',
      _isNew: false
    }));

    bulkEditForm.reset({
      bore_size_id: group.boreSize.id,
      slabs
    });

    setBulkEditMode(group.boreSize.id);
    setExpandedGroups(prev => new Set([...Array.from(prev), group.boreSize.id]));
  };

  const handleCancelBulkEdit = () => {
    setBulkEditMode(null);
    bulkEditForm.reset();
  };

  const handleAddSlabInBulkEdit = () => {
    const currentSlabs = bulkEditForm.getValues('slabs');
    const lastSlab = currentSlabs.length > 0 ? currentSlabs[currentSlabs.length - 1] : null;
    const suggestedFromSlab = lastSlab ? lastSlab.to_slab : 0;
    const suggestedToSlab = suggestedFromSlab ? suggestedFromSlab + 50 : 50;

    append({
      from_slab: suggestedFromSlab || 0,
      to_slab: suggestedToSlab,
      rate: lastSlab?.rate || 0,
      is_cancelled: 'N',
      _isNew: true
    });
  };

  const validateSlabRanges = (slabs: BulkEditSlab[]): string | null => {
    // Sort slabs by from_slab
    const sortedSlabs = [...slabs].sort((a, b) => a.from_slab - b.from_slab);

    for (let i = 0; i < sortedSlabs.length; i++) {
      const current = sortedSlabs[i];

      // Check if to_slab > from_slab
      if (current.to_slab <= current.from_slab) {
        return `Slab ${i + 1}: "To" value must be greater than "From" value`;
      }

      // Check for overlapping with next slab
      if (i < sortedSlabs.length - 1) {
        const next = sortedSlabs[i + 1];

        if (current.to_slab > next.from_slab) {
          return `Slabs ${i + 1} and ${i + 2} have overlapping ranges`;
        }
      }
    }

    return null;
  };

  const handleBulkEditSave = async (data: BulkEditForm) => {
    try {
      setSubmitting(true);

      // Validate ranges
      const validationError = validateSlabRanges(data.slabs);
      if (validationError) {
        showToast.error(validationError);
        setSubmitting(false);
        return;
      }

      // Prepare slabs data for bulk update
      const slabsData = data.slabs.map(slab => ({
        id: slab.id,
        from_slab: slab.from_slab,
        to_slab: slab.to_slab,
        rate: slab.rate,
        is_cancelled: slab.is_cancelled,
        _isNew: slab._isNew
      }));

      // Send all slabs in one bulk update request
      await priceSlabService.bulkUpdatePriceSlabs(data.bore_size_id, slabsData);

      showToast.success(`Successfully saved ${data.slabs.length} price slabs`);
      setBulkEditMode(null);
      loadPriceSlabs(tableParams);
    } catch (error: any) {
      console.error('Bulk edit error:', error);
      showToast.error(error.response?.data?.message || 'Failed to save price slabs');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkCreateSave = async (data: BulkEditForm) => {
    try {
      setSubmitting(true);

      // Validate bore size is selected
      if (!data.bore_size_id) {
        showToast.error('Please select a bore size');
        setSubmitting(false);
        return;
      }

      // Validate ranges
      const validationError = validateSlabRanges(data.slabs);
      if (validationError) {
        showToast.error(validationError);
        setSubmitting(false);
        return;
      }

      // Prepare slabs data for bulk create (all are new)
      const slabsData = data.slabs.map(slab => ({
        from_slab: slab.from_slab,
        to_slab: slab.to_slab,
        rate: slab.rate,
        is_cancelled: slab.is_cancelled,
        _isNew: true
      }));

      // Send all slabs in one bulk update request
      await priceSlabService.bulkUpdatePriceSlabs(data.bore_size_id, slabsData);

      showToast.success(`Successfully created ${data.slabs.length} price slabs`);
      handleCloseModal();
      loadPriceSlabs(tableParams);
    } catch (error: any) {
      console.error('Bulk create error:', error);
      showToast.error(error.response?.data?.message || 'Failed to create price slabs');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (priceSlab: PriceSlab) => {
    setDeleteConfirm({ show: true, priceSlab });
  };

  const handleDeleteSlabInBulkEdit = (index: number) => {
    const slab = fields[index] as any;

    // If it's a new slab, just remove it from the form
    if (slab._isNew) {
      remove(index);
      return;
    }

    // If it's an existing slab, show confirmation
    if (slab.id) {
      setDeleteConfirm({
        show: true,
        priceSlab: {
          id: slab.id,
          from_slab: slab.from_slab,
          to_slab: slab.to_slab,
          rate: slab.rate,
          is_cancelled: slab.is_cancelled,
          bore_size_id: bulkEditForm.getValues('bore_size_id')
        } as PriceSlab
      });
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.priceSlab) return;

    try {
      setDeleting(true);
      await priceSlabService.deletePriceSlab(deleteConfirm.priceSlab.id);
      showToast.success('Price slab deleted successfully');
      setDeleteConfirm({ show: false, priceSlab: null });

      // If in bulk edit mode, remove from form
      if (bulkEditMode) {
        const slabs = bulkEditForm.getValues('slabs');
        const indexToRemove = slabs.findIndex((s: any) => s.id === deleteConfirm.priceSlab?.id);
        if (indexToRemove !== -1) {
          remove(indexToRemove);
        }
      }

      loadPriceSlabs(tableParams);
    } catch (error: any) {
      console.error('Error deleting price slab:', error);
      showToast.error(error.response?.data?.message || 'Failed to delete price slab');
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPriceSlab(null);
    setSelectedBoreSizeForAdd(null);
    reset();
    bulkEditForm.reset();
  };

  const onSubmit = async (data: PriceSlabFormDataWithEdit) => {
    const submitFunction = async (formData: PriceSlabFormDataWithEdit) => {
      setSubmitting(true);
      try {
        const { isEdit, ...priceSlabData } = formData;

        if (editingPriceSlab) {
          await priceSlabService.updatePriceSlab(editingPriceSlab.id, priceSlabData);
          showToast.success('Price slab updated successfully');
        } else {
          await priceSlabService.createPriceSlab(priceSlabData);
          showToast.success('Price slab created successfully');
        }

        handleCloseModal();
        loadPriceSlabs(tableParams);
      } finally {
        setSubmitting(false);
      }
    };

    try {
      await protectedSubmit(data, submitFunction);
    } catch (error: any) {
      console.error('Form submission error:', error);

      if (error.response?.data?.errors) {
        const backendErrors = error.response.data.errors;
        Object.keys(backendErrors).forEach((field) => {
          setError(field as any, {
            type: 'server',
            message: backendErrors[field]
          });
        });
      } else {
        showToast.error(error.response?.data?.message || error.message || 'Failed to save price slab');
      }

      setSubmitting(false);
    }
  };

  // Helper functions
  const formatCurrency = (amount: number | null) => {
    if (amount === null || amount === undefined) return 'N/A';
    return `₹${amount.toLocaleString()}`;
  };

  const getStatusBadge = (isCancelled: string | null) => {
    return isCancelled === 'Y' ? (
      <Badge variant="error">Cancelled</Badge>
    ) : (
      <Badge variant="success">Active</Badge>
    );
  };

  const getRateColor = (rate: number, minRate: number, maxRate: number) => {
    if (minRate === maxRate) return 'bg-blue-500';
    const percentage = ((rate - minRate) / (maxRate - minRate)) * 100;
    if (percentage < 33) return 'bg-green-500';
    if (percentage < 66) return 'bg-yellow-500';
    return 'bg-orange-500';
  };

  // Status options
  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  // Check for authorization
  if (user && user.role !== 'ADMIN') {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center p-8 bg-white rounded-2xl shadow-lg">
            <div className="text-6xl mb-4">🔒</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Admin Access Only</h2>
            <p className="text-gray-600 mb-4">You do not have permission to access this page.</p>
            <p className="text-sm text-gray-500">Only Administrator can manage Price Slabs.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Price Slab Management</h1>
            <p className="text-sm text-gray-600 mt-1">
              Manage bore size pricing tiers grouped by bore size
            </p>
          </div>
          <div className="flex items-center gap-4">
            {isMobile && (
              <Button
                onClick={() => setShowMobileFilters(true)}
                variant="outline"
                className="flex items-center gap-2"
              >
                <FunnelIcon className="w-5 h-5" />
                Filters
              </Button>
            )}
            {user?.role === 'ADMIN' && (
              <Button onClick={handleCreate} className="flex items-center gap-2">
                <PlusIcon className="w-5 h-5" />
                Add Price Slab
              </Button>
            )}
          </div>
        </div>

        {/* Search and Filters - Desktop */}
        {!isMobile && (
          <div className="mb-6 space-y-4">
            <div className="flex gap-4">
              <div className="flex-1 max-w-md">
                <Input
                  placeholder="Search by bore size..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="w-full"
                />
              </div>
              <div className="w-48">
                <Select
                  placeholder="All Bore Sizes"
                  value={boreSizes.find(bs => bs.id === tableParams.boreSizeId) ?
                    { value: tableParams.boreSizeId?.toString(), label: boreSizes.find(bs => bs.id === tableParams.boreSizeId)?.size }
                    : null}
                  onChange={(option: any) => handleBoreSizeFilter(option?.value ? parseInt(option.value) : undefined)}
                  options={boreSizes.map(bs => ({ value: bs.id.toString(), label: bs.size }))}
                  isClearable
                />
              </div>
              <div className="w-44">
                <Select
                  placeholder="All Status"
                  value={statusOptions.find(opt => opt.value === tableParams.status) || null}
                  onChange={(option: any) => handleStatusFilter(option?.value || 'all')}
                  options={statusOptions}
                  isClearable
                />
              </div>
            </div>
          </div>
        )}

        {/* Mobile Filters Sidebar */}
        {isMobile && showMobileFilters && (
          <>
            <div
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setShowMobileFilters(false)}
            />

            <div className="fixed top-0 right-0 h-full w-80 bg-white z-50 shadow-xl flex flex-col">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 p-4 space-y-4 overflow-y-auto">
                <Input
                  placeholder="Search by bore size..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="w-full"
                />

                <Select
                  label="Bore Size"
                  placeholder="All Bore Sizes"
                  value={boreSizes.find(bs => bs.id === tableParams.boreSizeId) ?
                    { value: tableParams.boreSizeId?.toString(), label: boreSizes.find(bs => bs.id === tableParams.boreSizeId)?.size }
                    : null}
                  onChange={(option: any) => handleBoreSizeFilter(option?.value ? parseInt(option.value) : undefined)}
                  options={boreSizes.map(bs => ({ value: bs.id.toString(), label: bs.size }))}
                  isClearable
                  usePortal={false}
                />

                <Select
                  label="Status"
                  placeholder="All Status"
                  value={statusOptions.find(opt => opt.value === tableParams.status) || null}
                  onChange={(option: any) => handleStatusFilter(option?.value || 'all')}
                  options={statusOptions}
                  isClearable
                  usePortal={false}
                />
              </div>

              <div className="p-4 border-t border-gray-200">
                <Button
                  onClick={() => setShowMobileFilters(false)}
                  className="w-full"
                >
                  Apply Filters
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Grouped Price Slabs */}
        <div className="flex-1 min-h-0 overflow-y-auto bg-white rounded-2xl shadow-medium border border-gray-200 p-4 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center p-6 bg-white rounded-2xl shadow-strong border border-gray-200">
                <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
                <p className="text-gray-600 text-sm font-medium mt-3">Loading price slabs...</p>
              </div>
            </div>
          ) : groupedPriceSlabs.length === 0 ? (
            <div className="text-center py-12">
              <CurrencyDollarIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No Price Slabs Found</h3>
              <p className="text-gray-500 mb-4">Start by adding price slabs for your bore sizes</p>
              {user?.role === 'ADMIN' && (
                <Button onClick={handleCreate} className="flex items-center gap-2 mx-auto">
                  <PlusIcon className="w-5 h-5" />
                  Add First Price Slab
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {groupedPriceSlabs.map((group) => {
                const isExpanded = expandedGroups.has(group.boreSize.id);
                const isInBulkEditMode = bulkEditMode === group.boreSize.id;

                return (
                  <div
                    key={group.boreSize.id}
                    className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden"
                  >
                    {/* Group Header */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
                      <div className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4 flex-1">
                            <button
                              onClick={() => toggleGroup(group.boreSize.id)}
                              className="p-2 hover:bg-white/50 rounded-lg transition-colors"
                            >
                              {isExpanded ? (
                                <ChevronUpIcon className="w-5 h-5 text-gray-600" />
                              ) : (
                                <ChevronDownIcon className="w-5 h-5 text-gray-600" />
                              )}
                            </button>

                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <h3 className="text-lg font-bold text-gray-800">
                                  {group.boreSize.size}
                                </h3>
                                <Badge variant="info">{group.totalSlabs} Slabs</Badge>
                              </div>

                              {group.totalSlabs > 0 && (
                                <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-600">
                                  <div className="flex items-center gap-1">
                                    <ChartBarIcon className="w-4 h-4" />
                                    <span>Coverage: 0-{group.maxDepth} ft</span>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Rate Range:</span>{' '}
                                    <span className="font-medium text-green-600">
                                      {formatCurrency(group.minRate)}
                                    </span>
                                    {' - '}
                                    <span className="font-medium text-orange-600">
                                      {formatCurrency(group.maxRate)}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {user?.role === 'ADMIN' && !isInBulkEditMode && group.totalSlabs > 0 && (
                            <Button
                              onClick={() => handleBulkEdit(group)}
                              size="sm"
                              variant="outline"
                              className="flex items-center gap-2"
                            >
                              <PencilSquareIcon className="w-4 h-4" />
                              Bulk Edit
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Group Content */}
                    {isExpanded && (
                      <div className="p-4">
                        {isInBulkEditMode ? (
                          // Bulk Edit Mode
                          <form onSubmit={bulkEditForm.handleSubmit(handleBulkEditSave as any)}>
                            <div className="space-y-2">
                              {fields.map((field, index) => (
                                <div
                                  key={field.id}
                                  className="grid grid-cols-12 gap-2 p-3 bg-gray-50 rounded-lg items-start"
                                >
                                  {/* Hidden ID field */}
                                  <input
                                    type="hidden"
                                    {...bulkEditForm.register(`slabs.${index}.id`, { valueAsNumber: true })}
                                  />
                                  {/* Hidden _isNew field */}
                                  <input
                                    type="hidden"
                                    {...bulkEditForm.register(`slabs.${index}._isNew`)}
                                  />

                                  {/* Slab Number */}
                                  <div className="col-span-1 flex items-center justify-center pt-6">
                                    <div className="w-7 h-7 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-xs">
                                      {index + 1}
                                    </div>
                                  </div>

                                  {/* From Slab */}
                                  <div className="col-span-2">
                                    <Input
                                      label="From (ft)"
                                      type="number"
                                      step="1"
                                      min="0"
                                      {...bulkEditForm.register(`slabs.${index}.from_slab`, { valueAsNumber: true })}
                                      error={bulkEditForm.formState.errors.slabs?.[index]?.from_slab?.message}
                                      disabled={submitting}
                                    />
                                  </div>

                                  {/* To Slab */}
                                  <div className="col-span-2">
                                    <Input
                                      label="To (ft)"
                                      type="number"
                                      step="1"
                                      min="0"
                                      {...bulkEditForm.register(`slabs.${index}.to_slab`, { valueAsNumber: true })}
                                      error={bulkEditForm.formState.errors.slabs?.[index]?.to_slab?.message}
                                      disabled={submitting}
                                    />
                                  </div>

                                  {/* Rate */}
                                  <div className="col-span-2">
                                    <Input
                                      label="Rate (₹)"
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      {...bulkEditForm.register(`slabs.${index}.rate`, { valueAsNumber: true })}
                                      error={bulkEditForm.formState.errors.slabs?.[index]?.rate?.message}
                                      disabled={submitting}
                                    />
                                  </div>

                                  {/* Status */}
                                  <div className="col-span-3">
                                    <Controller
                                      name={`slabs.${index}.is_cancelled`}
                                      control={bulkEditForm.control}
                                      render={({ field: statusField }) => (
                                        <Select
                                          label="Status"
                                          value={statusField.value === 'Y' ?
                                            { value: 'Y', label: 'Cancelled' } :
                                            { value: 'N', label: 'Active' }}
                                          onChange={(option: any) => statusField.onChange(option?.value || 'N')}
                                          options={[
                                            { value: 'N', label: 'Active' },
                                            { value: 'Y', label: 'Cancelled' }
                                          ]}
                                          isDisabled={submitting}
                                          isClearable={false}
                                        />
                                      )}
                                    />
                                  </div>

                                  {/* Delete Button */}
                                  <div className="col-span-2 flex items-end justify-center pb-2">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDeleteSlabInBulkEdit(index)}
                                      className="text-red-600 hover:text-red-800 hover:bg-red-50 p-2"
                                      disabled={submitting}
                                    >
                                      <TrashIcon className="w-5 h-5" />
                                    </Button>
                                  </div>
                                </div>
                              ))}

                              {/* Add Slab Button */}
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={handleAddSlabInBulkEdit}
                                className="flex items-center gap-2"
                                disabled={submitting}
                              >
                                <PlusIcon className="w-4 h-4" />
                                Add Another Slab
                              </Button>

                              {/* Action Buttons */}
                              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={handleCancelBulkEdit}
                                  disabled={submitting}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  type="submit"
                                  loading={submitting}
                                  className="flex items-center gap-2"
                                >
                                  <CheckIcon className="w-4 h-4" />
                                  Save All Changes
                                </Button>
                              </div>
                            </div>
                          </form>
                        ) : (
                          // View Mode
                          group.slabs.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                              <p>No price slabs defined for this bore size</p>
                              <p className="text-sm mt-1">Click "Add Slab" to create the first one</p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {group.slabs.map((slab, index) => (
                                <div
                                  key={slab.id}
                                  className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                >
                                  {/* Slab Number */}
                                  <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                                    {index + 1}
                                  </div>

                                  {/* Depth Range */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="text-sm font-medium text-gray-600">
                                        Depth Range:
                                      </span>
                                      <span className="font-bold text-gray-900">
                                        {slab.from_slab} - {slab.to_slab} ft
                                      </span>
                                    </div>

                                    {/* Visual Range Bar */}
                                    <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                                      <div
                                        className={`absolute left-0 top-0 h-full ${getRateColor(
                                          slab.rate || 0,
                                          group.minRate,
                                          group.maxRate
                                        )}`}
                                        style={{
                                          width: `${((slab.to_slab || 0) - (slab.from_slab || 0)) / group.maxDepth * 100}%`,
                                          left: `${(slab.from_slab || 0) / group.maxDepth * 100}%`
                                        }}
                                      />
                                    </div>
                                  </div>

                                  {/* Rate */}
                                  <div className="flex-shrink-0 text-right">
                                    <div className="text-sm text-gray-600">Rate</div>
                                    <div className="text-lg font-bold text-green-600">
                                      {formatCurrency(slab.rate)}
                                    </div>
                                    <div className="text-xs text-gray-500">per ft</div>
                                  </div>

                                  {/* Status */}
                                  <div className="flex-shrink-0">
                                    {getStatusBadge(slab.is_cancelled)}
                                  </div>

                                  {/* Actions */}
                                  {user?.role === 'ADMIN' && (
                                    <div className="flex-shrink-0 flex items-center gap-2">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleEdit(slab)}
                                        className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                      >
                                        <PencilIcon className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDelete(slab)}
                                        className="text-red-600 hover:text-red-800 hover:bg-red-50"
                                      >
                                        <TrashIcon className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Create/Edit Modal */}
        <Modal
          isOpen={showModal}
          onClose={handleCloseModal}
          title={editingPriceSlab ? 'Edit Price Slab' : 'Add Price Slabs'}
          size="lg"
        >
          <div className="flex gap-6">
            {/* Form Section */}
            <div className={existingSlabsForSelectedBoreSize.length > 0 && editingPriceSlab ? 'flex-1' : 'w-full'}>
              {!editingPriceSlab ? (
                // Bulk Create Form
                <form onSubmit={bulkEditForm.handleSubmit(handleBulkCreateSave as any)} className="space-y-4">
                  <Controller
                    name="bore_size_id"
                    control={bulkEditForm.control}
                    render={({ field }) => (
                      <Select
                        label="Bore Size *"
                        placeholder="Select bore size"
                        value={boreSizes.find(bs => bs.id === field.value) ?
                          { value: field.value.toString(), label: boreSizes.find(bs => bs.id === field.value)?.size || '' }
                          : null}
                        onChange={(option: any) => field.onChange(option ? parseInt(option.value) : 0)}
                        options={boreSizes.map(bs => ({ value: bs.id.toString(), label: bs.size }))}
                        isDisabled={submitting}
                        isClearable={false}
                      />
                    )}
                  />

                  <div className="space-y-2">
                    {fields.map((field, index) => (
                      <div
                        key={field.id}
                        className="grid grid-cols-12 gap-2 p-3 bg-gray-50 rounded-lg items-start"
                      >
                        {/* Hidden fields */}
                        <input type="hidden" {...bulkEditForm.register(`slabs.${index}._isNew`)} />

                        {/* Slab Number */}
                        <div className="col-span-1 flex items-center justify-center pt-6">
                          <div className="w-7 h-7 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-xs">
                            {index + 1}
                          </div>
                        </div>

                        {/* From Slab */}
                        <div className="col-span-2">
                          <Input
                            label="From (ft)"
                            type="number"
                            step="1"
                            min="0"
                            {...bulkEditForm.register(`slabs.${index}.from_slab`, { valueAsNumber: true })}
                            error={bulkEditForm.formState.errors.slabs?.[index]?.from_slab?.message}
                            disabled={submitting}
                          />
                        </div>

                        {/* To Slab */}
                        <div className="col-span-2">
                          <Input
                            label="To (ft)"
                            type="number"
                            step="1"
                            min="0"
                            {...bulkEditForm.register(`slabs.${index}.to_slab`, { valueAsNumber: true })}
                            error={bulkEditForm.formState.errors.slabs?.[index]?.to_slab?.message}
                            disabled={submitting}
                          />
                        </div>

                        {/* Rate */}
                        <div className="col-span-2">
                          <Input
                            label="Rate (₹)"
                            type="number"
                            step="0.01"
                            min="0"
                            {...bulkEditForm.register(`slabs.${index}.rate`, { valueAsNumber: true })}
                            error={bulkEditForm.formState.errors.slabs?.[index]?.rate?.message}
                            disabled={submitting}
                          />
                        </div>

                        {/* Status */}
                        <div className="col-span-3">
                          <Controller
                            name={`slabs.${index}.is_cancelled`}
                            control={bulkEditForm.control}
                            render={({ field: statusField }) => (
                              <Select
                                label="Status"
                                value={statusField.value === 'Y' ?
                                  { value: 'Y', label: 'Cancelled' } :
                                  { value: 'N', label: 'Active' }}
                                onChange={(option: any) => statusField.onChange(option?.value || 'N')}
                                options={[
                                  { value: 'N', label: 'Active' },
                                  { value: 'Y', label: 'Cancelled' }
                                ]}
                                isDisabled={submitting}
                                isClearable={false}
                              />
                            )}
                          />
                        </div>

                        {/* Delete Button */}
                        <div className="col-span-2 flex items-end justify-center pb-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => remove(index)}
                            className="text-red-600 hover:text-red-800 hover:bg-red-50 p-2"
                            disabled={submitting || fields.length === 1}
                          >
                            <TrashIcon className="w-5 h-5" />
                          </Button>
                        </div>
                      </div>
                    ))}

                    {/* Add Slab Button */}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAddSlabInBulkEdit}
                      className="flex items-center gap-2"
                      disabled={submitting}
                    >
                      <PlusIcon className="w-4 h-4" />
                      Add Another Slab
                    </Button>
                  </div>

                  <div className="flex gap-3 justify-end pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCloseModal}
                      disabled={submitting}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" loading={submitting}>
                      Create All Slabs
                    </Button>
                  </div>
                </form>
              ) : (
                // Single Create/Edit Form
                <form onSubmit={handleSubmit(onSubmit as any)} className="space-y-4">
                <Controller
                  name="bore_size_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      label="Bore Size *"
                      placeholder="Select bore size"
                      value={boreSizes.find(bs => bs.id === field.value) ?
                        { value: field.value.toString(), label: boreSizes.find(bs => bs.id === field.value)?.size || '' }
                        : null}
                      onChange={(option: any) => field.onChange(option ? parseInt(option.value) : 0)}
                      options={boreSizes.map(bs => ({ value: bs.id.toString(), label: bs.size }))}
                      error={errors.bore_size_id?.message}
                      isDisabled={submitting || !!selectedBoreSizeForAdd}
                      isClearable={false}
                    />
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="From Slab (ft) *"
                    type="number"
                    step="1"
                    min="0"
                    {...register('from_slab', { valueAsNumber: true })}
                    error={errors.from_slab?.message}
                    disabled={submitting}
                  />

                  <Input
                    label="To Slab (ft) *"
                    type="number"
                    step="1"
                    min="0"
                    {...register('to_slab', { valueAsNumber: true })}
                    error={errors.to_slab?.message}
                    disabled={submitting}
                  />
                </div>

                <Input
                  label="Rate (₹) *"
                  type="number"
                  step="0.01"
                  min="0"
                  {...register('rate', { valueAsNumber: true })}
                  error={errors.rate?.message}
                  disabled={submitting}
                />

                <Controller
                  name="is_cancelled"
                  control={control}
                  render={({ field }) => (
                    <Select
                      label="Status"
                      placeholder="Select status"
                      value={field.value === 'Y' ?
                        { value: 'Y', label: 'Cancelled' } :
                        { value: 'N', label: 'Active' }}
                      onChange={(option: any) => field.onChange(option?.value || 'N')}
                      options={[
                        { value: 'N', label: 'Active' },
                        { value: 'Y', label: 'Cancelled' }
                      ]}
                      isDisabled={submitting}
                      isClearable={false}
                    />
                  )}
                />

                <div className="flex gap-3 justify-end pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCloseModal}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" loading={submitting}>
                    {editingPriceSlab ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
              )}
            </div>

            {/* Existing Slabs Sidebar */}
            {existingSlabsForSelectedBoreSize.length > 0 && editingPriceSlab && (
              <div className="w-80 border-l border-gray-200 pl-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  Existing Slabs for {boreSizes.find(bs => bs.id === selectedBoreSizeId)?.size}
                </h4>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {existingSlabsForSelectedBoreSize.map((slab, index) => (
                    <div
                      key={slab.id}
                      className={`p-3 rounded-lg border ${
                        editingPriceSlab?.id === slab.id
                          ? 'bg-blue-50 border-blue-300'
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-gray-500">#{index + 1}</span>
                        {getStatusBadge(slab.is_cancelled)}
                      </div>
                      <div className="text-sm font-medium text-gray-900">
                        {slab.from_slab} - {slab.to_slab} ft
                      </div>
                      <div className="text-sm font-bold text-green-600">
                        {formatCurrency(slab.rate)}/ft
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-800">
                    <strong>Tip:</strong> When adding a new slab, ensure the range doesn't overlap with existing slabs.
                  </p>
                </div>
              </div>
            )}
          </div>
        </Modal>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={deleteConfirm.show}
          onClose={() => setDeleteConfirm({ show: false, priceSlab: null })}
          onConfirm={confirmDelete}
          title="Delete Price Slab"
          message={`Are you sure you want to delete the price slab for "${deleteConfirm.priceSlab?.boreSize?.size}" (${deleteConfirm.priceSlab?.from_slab}-${deleteConfirm.priceSlab?.to_slab} ft)? This action cannot be undone.`}
          confirmText="Delete"
          variant="danger"
          loading={deleting}
        />
      </div>
    </MainLayout>
  );
};

export default PriceSlabManagement;
